/**
 * Input system shared types — enums, interfaces, and data shapes.
 *
 * Kept in a separate file so InputReader.ts and InputSystem.ts can
 * import from here without circular dependencies.
 */

// ─── Device Classification ────────────────────────────────────────────────────

export const enum DeviceType {
  KeyboardMouse,
  Gamepad,
  Touch,
  XRController,
  XRHand,
  VisionProGaze,
}

// ─── Gesture Recognition ─────────────────────────────────────────────────────

export const enum GestureType {
  Pinch,
  Grab,
  Point,
  OpenPalm,
  ThumbsUp,
  ThumbsDown,
  Peace,
  FingerGun,
  Rock,
}

export const enum GestureState {
  None,
  Started,
  Held,
  Ended,
}

// ─── Gamepad Controller Identification ───────────────────────────────────────

export const enum ControllerType {
  Xbox,
  PlayStation,
  Switch,
  Quest,
  Generic,
}

// ─── Touch Zone Configuration ────────────────────────────────────────────────

export interface TouchZoneConfig {
  name: string;
  region: { x: number; y: number; width: number; height: number };
  type: 'joystick' | 'button' | 'swipe' | 'free';
  /** Optional action name to map this zone to */
  action?: string;
}

// ─── Touch Joystick State ────────────────────────────────────────────────────

export interface TouchJoystickState {
  active: boolean;
  originX: number;
  originY: number;
  currentX: number;
  currentY: number;
  /** Normalised X value in [-1, 1] */
  valueX: number;
  /** Normalised Y value in [-1, 1] */
  valueY: number;
}

// ─── Gesture Event ───────────────────────────────────────────────────────────

export interface GestureEvent {
  type: GestureType;
  state: GestureState;
  /** 0 = left hand, 1 = right hand */
  hand: 0 | 1;
  /** Recognition confidence in [0, 1] */
  confidence: number;
}

// ─── Haptic Feedback Preset ──────────────────────────────────────────────────

export interface HapticPreset {
  name: string;
  weakMagnitude: number;
  strongMagnitude: number;
  /** Duration in milliseconds */
  duration: number;
}

// ─── Gamepad Snapshot ────────────────────────────────────────────────────────

export interface GamepadButtonSnapshot {
  pressed: boolean;
  touched: boolean;
  value: number;
}

export interface GamepadSnapshot {
  index: number;
  id: string;
  connected: boolean;
  buttons: GamepadButtonSnapshot[];
  axes: number[];
  timestamp: number;
}

// ─── Pointer Tracking ────────────────────────────────────────────────────────

export interface PointerData {
  id: number;
  x: number;
  y: number;
  type: string;
  pressure: number;
  startX: number;
  startY: number;
  startTime: number;
  /** Set to true when this pointer participates in a multi-touch gesture. */
  wasMultiTouch?: boolean;
}

// ─── Device Capability Signals ───────────────────────────────────────────────

export interface DeviceSignals {
  hasKeyboard: boolean;
  hasMouse: boolean;
  hasGamepad: boolean;
  hasTouch: boolean;
  hasXR: boolean;
  hasVisionPro: boolean;
}
